import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ColumnPickerDialog from "@/components/ColumnPickerDialog";
import { formatApiError } from "@/lib/api";
import { fetchExportColumns, lastFormat, rememberFormat } from "@/lib/exportColumns";

/**
 * One Download button for a server-rendered document: it asks which columns, then opens the
 * file. `href(format, keys)` builds the address — the caller owns the filters, this owns the
 * choice of columns, and the server is told both in the query string.
 *
 * A disabled button says WHY beside itself, in text: a tooltip is invisible on a phone, and
 * a greyed-out button with no reason reads as broken.
 */
export default function DocumentDownload({
  docKey, href, label = "Download", variant = "outline", size, className,
  disabled = false, disabledReason, initialFormat, testid,
}) {
  const [doc, setDoc] = useState(null);
  const [open, setOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);

  const start = async () => {
    setPreparing(true);
    try {
      const found = (await fetchExportColumns())[docKey];
      if (!found) throw new Error(`No columns for ${docKey}`);
      setDoc(found);
      setOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not load the columns."));
    } finally {
      setPreparing(false);
    }
  };

  const download = (format, keys) => {
    rememberFormat(docKey, format);
    window.open(href(format, keys));
  };

  const button = (
    <Button variant={variant} size={size} className={className} onClick={start}
            disabled={disabled || preparing} aria-busy={preparing || undefined} data-testid={testid}>
      {preparing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
      {preparing ? "Preparing…" : label}
    </Button>
  );
  const reason = disabled && disabledReason;

  return (
    <>
      {reason ? (
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          {button}
          <span className="text-xs text-muted-foreground" data-testid={testid ? `${testid}-reason` : undefined}>
            {disabledReason}
          </span>
        </span>
      ) : button}
      <ColumnPickerDialog open={open} onClose={() => setOpen(false)} docKey={docKey} doc={doc}
                          initialFormat={initialFormat || lastFormat(docKey, doc?.formats)}
                          onDownload={download} />
    </>
  );
}
